import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import Retracked from 'js/app/retracked';

import { StyleSheet, css } from '@coursera/coursera-ui';

import Submit from 'bundles/authentication/components/Submit';
import {
  EMAIL_CONSTRAINT,
  FIRSTNAME_CONSTRAINT,
  HANDLED_ERRORS,
  LASTNAME_CONSTRAINT,
  PASSWORD_CONSTRAINT,
  RECAPTCHA_ACTIONS,
  SIGNUP_REDESIGN_RECAPTCHA_ID,
  TOU_CHECKBOX_CONSTRAINT,
  emailRequirementsError,
  existingEmailError,
  nameRequirementsError,
  passwordRequirementsError,
  responseMessageFactory,
} from 'bundles/authentication/constants';
import Alert from 'bundles/authentication/shared/Alert';
import EmailInput from 'bundles/authentication/shared/EmailInput';
import Form from 'bundles/authentication/shared/Form';
import NameInput from 'bundles/authentication/shared/NameInput';
import PasswordInput from 'bundles/authentication/shared/PasswordInput';
import TermsOfUseCheckbox from 'bundles/authentication/shared/TermsOfUseCheckbox';
import * as actions from 'bundles/authentication/shared/actions';
import { useAddProfileConsent } from 'bundles/authentication/shared/hooks/useAddProfileConsent';
import { useRecaptcha } from 'bundles/authentication/shared/hooks/useRecaptcha';
import type {
  ErrorResponse,
  EventData,
  SignupSharedProps,
  SuccessResponse,
} from 'bundles/authentication/shared/types/sharedTypes';
import {
  generateImpressionForEnableLegalConsolidationAuthFlow,
  shouldShowCheckboxTermsOfUse,
} from 'bundles/authentication/utils';
import { isMisspelledDomain } from 'bundles/authentication/utils/emailVerificationUtils';
import withSingleTracked from 'bundles/common/components/withSingleTracked';
import GrowthAcquisitionExperiments from 'bundles/epic/clients/GrowthAcquisition';
import { INTERACTION_EVENTS_MAP } from 'bundles/expression-of-interest/constants';
import { useEoiEventing } from 'bundles/expression-of-interest/utils/eventingUtils';
import { isDestinationDegree } from 'bundles/expression-of-interest/utils/util';
import { trackAuthAttempts } from 'bundles/user-account/utils/authTrackingUtils';

import _t from 'i18n!nls/authentication';

type Context = {
  _eventData: EventData;
};

type Props = SignupSharedProps & {
  extraFormContent?: React.ReactElement;
  slug: string;
  toFocus?: boolean;
  showSubmitButton?: boolean;
  trackingProductId: string;
  afterSubmit?: () => void;
  beforeSubmit?: () => void;
};

const TrackedSubmit = withSingleTracked({ type: 'BUTTON' })(Submit);

const styles = StyleSheet.create({
  container: {
    display: 'grid',
    gridGap: '12px',
    gridTemplateColumns: '1fr',
  },
  item: {
    ':nth-child(1)': {
      '-ms-grid-row': '1',
    },
    ':nth-child(2)': {
      '-ms-grid-row': '2',
    },
    ':nth-child(3)': {
      '-ms-grid-row': '3',
    },
    ':nth-child(4)': {
      '-ms-grid-row': '4',
    },
    ':nth-child(5)': {
      '-ms-grid-row': '5',
    },
  },
  reCAPTCHA: {
    display: 'flex',
    'justify-content': 'center',
    '&.checkbox': { 'margin-top': '8px' },
    ':nth-of-type(n) .grecaptcha-badge': { display: 'none !important' },
  },
  disclaimer: {
    margin: '0 auto !important',
  },
  submit: {
    marginTop: '12px',
    width: '100%',
  },
});

const SignupForm = (
  {
    navigateToLogin,
    onSuccess,
    extraFormContent,
    slug,
    toFocus,
    showSubmitButton = true,
    trackingProductId,
    afterSubmit,
    beforeSubmit,
  }: Props,
  { _eventData }: Context
) => {
  const isCheckbox = GrowthAcquisitionExperiments.get('useEnterpriseCheckboxReCaptchaSignup');
  const [message, setMessage] = useState<string | JSX.Element>('');
  const [isRunning, setIsRunning] = useState(false);

  const firstNameInputRef = useRef<HTMLInputElement | null>(null);
  const lastNameInputRef = useRef<HTMLInputElement | null>(null);
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const passwordInputRef = useRef<HTMLInputElement | null>(null);
  const touCheckboxInputRef = useRef<HTMLInputElement | null>(null);
  const recaptcha = useRecaptcha(SIGNUP_REDESIGN_RECAPTCHA_ID, {
    checkbox: isCheckbox,
    action: RECAPTCHA_ACTIONS.signup,
  });

  const { trackInteractEoi } = useEoiEventing(trackingProductId);
  const { v2TrackingName, v3EventKey } = INTERACTION_EVENTS_MAP.signupFormSubmitButton;
  const showCheckboxTermsOfUse = shouldShowCheckboxTermsOfUse();
  const addProfileConsent = useAddProfileConsent();

  useEffect(() => {
    firstNameInputRef.current = document.getElementById('firstname') as HTMLInputElement;
    lastNameInputRef.current = document.getElementById('lastname') as HTMLInputElement;
    emailInputRef.current = document.getElementById('email') as HTMLInputElement;
    passwordInputRef.current = document.getElementById('password') as HTMLInputElement;
    touCheckboxInputRef.current = document.getElementsByName('touCheckbox')[0] as HTMLInputElement;
    if (toFocus) {
      firstNameInputRef?.current?.focus();
    }
  }, [toFocus]);

  useEffect(() => {
    generateImpressionForEnableLegalConsolidationAuthFlow();
  }, []);

  /**
   * Handles errors that occur during the signup process by resetting the reCAPTCHA,
   * setting an appropriate error message, updating the UI, and focusing the input field
   * associated with the error.
   *
   * @param {ErrorResponse} response - The error response object containing the error code and other relevant information.
   */
  const handleError = (response: ErrorResponse) => {
    recaptcha?.reset();

    const { errorCode = '' } = response?.responseJSON || {};

    if (HANDLED_ERRORS.includes(errorCode)) {
      const messages = responseMessageFactory();

      if (errorCode === existingEmailError) {
        const errMessageForExistingAcc = messages[errorCode](navigateToLogin);
        setMessage(errMessageForExistingAcc);
        setIsRunning(false);
      } else {
        const msg = messages[errorCode as Exclude<keyof typeof messages, 'existingCourseraAccount'>];
        setMessage(msg);
        setIsRunning(false);
      }

      if (errorCode === nameRequirementsError) {
        nameInputRef.current?.focus();
      } else if (errorCode === existingEmailError || errorCode === emailRequirementsError) {
        emailInputRef.current?.focus();
      } else if (errorCode === passwordRequirementsError) {
        passwordInputRef.current?.focus();
      }
    } else {
      setMessage(_t('An unexpected error has occurred. Please try again'));
      setIsRunning(false);
    }
    afterSubmit?.();
  };

  /**
   * Handles a successful signup by setting a success message and calling the onSuccess function.
   *
   * @param {SuccessResponse} response - The success response object containing relevant information.
   *
   * @returns {ReturnType<typeof onSuccess>} The return value of the onSuccess function.
   */
  const handleSuccess = (response: SuccessResponse): ReturnType<typeof onSuccess> => {
    const successMsg = responseMessageFactory().success;

    setMessage(successMsg);

    if (showCheckboxTermsOfUse) addProfileConsent();

    return onSuccess(response);
  };

  /**
   * Handles the user signup process by validating the provided information, checking the reCAPTCHA token, and
   * calling the signup action. If the signup is successful, it handles the success response; otherwise, it handles
   * the error response.
   *
   * @param {Record<string, string>} values - An object containing the user's email, name, password, first name, and last name.
   * @returns {Promise<void | boolean | undefined>} Returns a promise that resolves to `undefined` when the function
   * execution is complete, `false` if the email domain is invalid, or a message if the reCAPTCHA token is missing.
   */
  const handleSignup = async (values: Record<string, string>): Promise<void | boolean | undefined> => {
    const { email: signupEmail, name, password, firstName, lastName } = values;

    const fullName = name?.trim() ?? `${firstName?.trim()} ${lastName?.trim()}`;

    const emailSplit = signupEmail.split('@');
    const emailProvider = emailSplit[emailSplit.length - 1];
    if (isMisspelledDomain(emailProvider)) {
      emailInputRef.current?.setCustomValidity(
        _t('Looks like you misspelled the domain of your email address. Please try again.')
      );
      emailInputRef.current?.reportValidity();
      return false;
    }

    const token = await recaptcha?.getResponse();

    if (!token) {
      const msg = isCheckbox ? responseMessageFactory().missingReCaptcha : '';
      return setMessage(msg);
    }

    setIsRunning(true);
    beforeSubmit?.();
    trackAuthAttempts(_eventData, 'signup');

    try {
      const response = await actions.signup({ email: signupEmail, name: fullName, password, token });
      trackInteractEoi(v3EventKey);

      handleSuccess(response as SuccessResponse);
    } catch (error) {
      handleError(error as ErrorResponse);
    }

    return undefined;
  };

  /**
   * Resets the displayed message when an input field is marked as invalid.
   */
  const onInvalid = () => {
    setMessage('');
  };

  /**
   * Tracks the focus event of a signup text input field for analytics purposes.
   *
   * @param {string} trackingName - The name of the input field being focused.
   */
  const onFocus = (trackingName: string) => {
    Retracked.trackComponent(_eventData, {}, trackingName, 'signup_text_input_focus');
  };

  /**
   * An object containing validation constraints for the firstName, lastName, email, and password fields.
   * Each constraint is a function that takes a string as an argument and returns a string
   * (indicating an error message) or a boolean (indicating that the input is valid).
   *
   * @type {Record<string, (arg: string) => string | boolean>}
   */
  const constraints: Record<string, (arg: string) => string | boolean> = {
    firstName: FIRSTNAME_CONSTRAINT,
    lastName: LASTNAME_CONSTRAINT,
    email: EMAIL_CONSTRAINT,
    password: PASSWORD_CONSTRAINT,
    touCheckbox: showCheckboxTermsOfUse ? TOU_CHECKBOX_CONSTRAINT : () => false,
  };

  return (
    <Form
      {...css('rc-SignupForm', styles.container)}
      constraints={constraints}
      name="signup"
      onSubmit={handleSignup}
      onInvalid={onInvalid}
      id="eoi-modal-signup-form"
    >
      {message && (
        <Alert
          role="status"
          aria-live="assertive"
          {...css(styles.item)}
          type={message === responseMessageFactory().success ? 'success' : 'error'}
        >
          {message}
        </Alert>
      )}

      <NameInput
        label={_t('First Name')}
        name="firstName"
        placeholder={_t('Enter your first name')}
        required
        ref={firstNameInputRef}
        onFocus={() => onFocus('first_name')}
        autoComplete="given-name"
        {...css(styles.item)}
      />
      <NameInput
        label={_t('Last Name')}
        name="lastName"
        placeholder={_t('Enter your last name')}
        required
        ref={lastNameInputRef}
        onFocus={() => onFocus('last_name')}
        autoComplete="family-name"
        {...css(styles.item)}
      />

      <EmailInput
        {...css(styles.item)}
        emailSpellingCheck
        required
        ref={emailInputRef}
        onFocus={() => onFocus('email')}
      />
      <PasswordInput
        {...css(styles.item)}
        hint={_t('Between 8 and 72 characters')}
        required
        ref={passwordInputRef}
        onFocus={() => onFocus('password')}
        autoComplete="new-password"
      />
      {extraFormContent}
      <div {...css(styles.reCAPTCHA)} id={SIGNUP_REDESIGN_RECAPTCHA_ID} data-size={!isCheckbox && 'invisible'} />
      {showCheckboxTermsOfUse && (
        <TermsOfUseCheckbox ref={touCheckboxInputRef} data-testid="signup-form-tou-checkbox" />
      )}
      {showSubmitButton && (
        <TrackedSubmit
          {...css(styles.submit, styles.item, styles.submit)}
          data-catchpoint="signup-form-submit-button"
          data-e2e="signup-form-submit-button"
          disabled={isRunning}
          trackingName={v2TrackingName}
        >
          {isDestinationDegree(slug) ? _t('Create account for free') : _t('Join for free')}
        </TrackedSubmit>
      )}
    </Form>
  );
};

SignupForm.contextTypes = {
  _eventData: PropTypes.object.isRequired,
};

export default Retracked.createTrackedContainer<Props>(() => ({
  namespace: {
    page: 'signup',
  },
}))(SignupForm);
